import type { SortingState, Updater } from '@tanstack/react-table'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSearchParams } from 'react-router'

import { useDebouncedValue } from '@/lib/use-debounced-value'

type SortOrder = 'asc' | 'desc'

/* every list endpoint caps pageSize at 100. */
const MAX_PAGE_SIZE = 100

function positiveInt(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null
  const parsed = Number(value)
  return parsed >= 1 ? parsed : null
}

/* `url` keeps page, sort and search in the query string so Back and reload
 * restore them. writes replace the entry, so Back leaves the list. */
export function useServerTable<TSortField extends string = string>(options?: {
  defaultPageSize?: number
  defaultSorting?: SortingState
  /** serialized domain filters, any change resets to page 1. */
  resetKey?: string
  url?: { sortFields: readonly string[] }
}) {
  const sortFields = options?.url?.sortFields
  const url = sortFields !== undefined
  const defaultPageSize = options?.defaultPageSize ?? 10
  const defaultSorting = options?.defaultSorting
  const [searchParams, setSearchParams] = useSearchParams()

  const [localPage, setLocalPage] = useState(1)
  const [localPageSize, setLocalPageSize] = useState(defaultPageSize)
  const [localSorting, setLocalSorting] = useState<SortingState>(
    defaultSorting ?? []
  )

  const urlSearch = searchParams.get('search') ?? ''
  const [search, setSearch] = useState(url ? urlSearch : '')
  const debouncedInput = useDebouncedValue(search, 200)

  const urlSort = searchParams.get('sort')
  const urlDesc = searchParams.get('order') === 'desc'
  const urlSorting = useMemo<SortingState>(
    () =>
      urlSort && sortFields?.includes(urlSort)
        ? [{ id: urlSort, desc: urlDesc }]
        : (defaultSorting ?? []),
    [sortFields, urlSort, urlDesc, defaultSorting]
  )

  const page = url ? (positiveInt(searchParams.get('page')) ?? 1) : localPage
  const pageSize = url
    ? Math.min(
        positiveInt(searchParams.get('pageSize')) ?? defaultPageSize,
        MAX_PAGE_SIZE
      )
    : localPageSize
  const sorting = url ? urlSorting : localSorting
  const debouncedSearch = url ? urlSearch : debouncedInput

  /* react router hands out a new setSearchParams on every url change. calling
   * it through a ref keeps writeParams stable, so the search effect below
   * doesn't re-run on Back and write the old term over the new url. */
  const latest = useRef({ setSearchParams, urlSearch })
  useLayoutEffect(() => {
    latest.current = { setSearchParams, urlSearch }
  })

  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      latest.current.setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          mutate(next)
          return next
        },
        { replace: true }
      )
    },
    []
  )

  const setPage = useCallback(
    (next: number) => {
      if (!url) return setLocalPage(next)
      writeParams(params =>
        next > 1 ? params.set('page', String(next)) : params.delete('page')
      )
    },
    [url, writeParams]
  )

  const setPageSize = useCallback(
    (next: number) => {
      if (!url) return setLocalPageSize(next)
      writeParams(params => {
        if (next === defaultPageSize) params.delete('pageSize')
        else params.set('pageSize', String(next))
        params.delete('page')
      })
    },
    [url, writeParams, defaultPageSize]
  )

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      if (!url) return setLocalSorting(updater)
      const next = typeof updater === 'function' ? updater(urlSorting) : updater
      writeParams(params => {
        const first = next[0]
        if (first) {
          params.set('sort', first.id)
          params.set('order', first.desc ? 'desc' : 'asc')
        } else {
          params.delete('sort')
          params.delete('order')
        }
        params.delete('page')
      })
    },
    [url, urlSorting, writeParams]
  )

  /* the hook's own search write comes back as a url change. remembering what it
   * wrote stops that echo from overwriting keys typed during the debounce. */
  const lastWrittenSearch = useRef(urlSearch)

  useEffect(() => {
    if (!url) return
    const trimmed = debouncedInput.trim()
    if (trimmed === latest.current.urlSearch) return
    lastWrittenSearch.current = trimmed
    writeParams(params => {
      if (trimmed) params.set('search', trimmed)
      else params.delete('search')
      params.delete('page')
    })
  }, [url, debouncedInput, writeParams])

  useEffect(() => {
    if (!url || urlSearch === lastWrittenSearch.current) return
    lastWrittenSearch.current = urlSearch
    setSearch(urlSearch)
  }, [url, urlSearch])

  /* values the hook ignored (a bad sort, page=-2, a blank search) would
   * otherwise stay in the address and disagree with the screen. */
  useEffect(() => {
    if (!url) return
    const urlPage = positiveInt(searchParams.get('page')) ?? 1
    const urlPageSize = Math.min(
      positiveInt(searchParams.get('pageSize')) ?? defaultPageSize,
      MAX_PAGE_SIZE
    )
    const rawSort = searchParams.get('sort')
    const validSort = rawSort && sortFields?.includes(rawSort) ? rawSort : null
    const rawSearch = searchParams.get('search')
    const canonical: Record<string, string | null> = {
      page: urlPage > 1 ? String(urlPage) : null,
      pageSize: urlPageSize !== defaultPageSize ? String(urlPageSize) : null,
      sort: validSort,
      order: validSort
        ? searchParams.get('order') === 'desc'
          ? 'desc'
          : 'asc'
        : null,
      search: rawSearch?.trim() ? rawSearch : null
    }
    const stale = Object.entries(canonical).filter(
      ([key, value]) => searchParams.get(key) !== value
    )
    if (stale.length === 0) return
    writeParams(params => {
      for (const [key, value] of stale) {
        if (value === null) params.delete(key)
        else params.set(key, value)
      }
    })
  }, [url, searchParams, sortFields, defaultPageSize, writeParams])

  /* a filter change after mount drops back to page 1. on mount it must not,
   * or a restored ?page=3 is wiped before the first fetch. */
  const resetKey = options?.resetKey
  const previousResetKey = useRef(resetKey)
  useEffect(() => {
    if (previousResetKey.current === resetKey) return
    previousResetKey.current = resetKey
    if (url) {
      if (searchParams.has('page')) writeParams(params => params.delete('page'))
    } else {
      setLocalPage(1)
    }
  }, [url, resetKey, searchParams, writeParams])

  const sort = sorting[0]?.id as TSortField | undefined
  const order: SortOrder | undefined = sorting[0]
    ? sorting[0].desc
      ? 'desc'
      : 'asc'
    : undefined

  useEffect(() => {
    if (!url) setLocalPage(1)
  }, [url, debouncedInput, localPageSize, sort, order])

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    sorting,
    setSorting,
    search,
    setSearch,
    debouncedSearch,
    sort,
    order
  }
}
